import { useEffect, useState } from 'react';
import { Award, Loader2 } from 'lucide-react';
import { api } from '../../lib/api';
import { getToken } from '../../lib/auth';
import { Link } from 'react-router-dom';

interface LoyaltyTierProps {
  title?: string;
  showProgress?: boolean;
  showNextTier?: boolean;
}

interface TierDetails {
  name: string;
  color: string;
  minPoints: number;
}

interface AccountData {
  pointsBalance: number;
  currentTierDetails: TierDetails | null;
  nextTierDetails: TierDetails | null;
  pointsToNextTier: number | null;
  tiers: TierDetails[];
}

export function LoyaltyTierBlock({ props }: { props: LoyaltyTierProps }) {
  const [account, setAccount] = useState<AccountData | null>(null);
  const [loading, setLoading] = useState(true);
  const authed = !!getToken();

  useEffect(() => {
    if (!authed) {
      setLoading(false);
      return;
    }
    api
      .get<{ enrolled: boolean; accounts: AccountData[] }>('/customer/loyalty')
      .then((res) => {
        if (res.data.enrolled && res.data.accounts.length > 0) {
          setAccount(res.data.accounts[0]);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [authed]);

  if (!authed) {
    return (
      <div className="bg-zinc-50 border border-zinc-200 rounded-xl px-6 py-8 text-center">
        <Award size={28} className="mx-auto mb-3 text-zinc-300" />
        <p className="text-sm text-zinc-500">
          <Link to="/login" className="underline hover:no-underline text-zinc-700 font-medium">
            Log in
          </Link>{' '}
          to see your loyalty info
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 size={20} className="animate-spin text-zinc-400" />
      </div>
    );
  }

  if (!account?.currentTierDetails) {
    return (
      <div className="bg-zinc-50 border border-zinc-200 rounded-xl px-6 py-8 text-center">
        <Award size={28} className="mx-auto mb-3 text-zinc-300" />
        <p className="text-sm text-zinc-500">No tier information available.</p>
      </div>
    );
  }

  const { currentTierDetails, nextTierDetails, pointsToNextTier, pointsBalance, tiers } = account;

  const prevMin = currentTierDetails.minPoints;
  const nextMin = nextTierDetails?.minPoints ?? null;
  const progress =
    nextMin !== null
      ? Math.min(100, Math.round(((pointsBalance - prevMin) / (nextMin - prevMin)) * 100))
      : 100;

  return (
    <div className="bg-white border border-zinc-100 rounded-2xl p-6 shadow-sm space-y-4">
      {props.title && <h3 className="text-base font-semibold text-zinc-900">{props.title}</h3>}

      {/* Current tier badge */}
      <div className="flex items-center gap-3">
        <span
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold"
          style={{
            backgroundColor: currentTierDetails.color + '20',
            color: currentTierDetails.color,
          }}
        >
          <Award size={14} />
          {currentTierDetails.name}
        </span>
        <span className="text-sm text-zinc-500">Current tier</span>
      </div>

      {/* Progress bar toward next tier */}
      {props.showProgress !== false && nextTierDetails && (
        <div className="space-y-1.5">
          <div className="flex justify-between text-xs text-zinc-500">
            <span>{currentTierDetails.name}</span>
            {props.showNextTier !== false && (
              <span>
                {(pointsToNextTier ?? 0).toLocaleString()} pts to{' '}
                <strong style={{ color: nextTierDetails.color }}>{nextTierDetails.name}</strong>
              </span>
            )}
          </div>
          <div className="h-2.5 rounded-full bg-zinc-100 overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{ width: `${progress}%`, backgroundColor: currentTierDetails.color }}
            />
          </div>
          <p className="text-xs text-zinc-400">{progress}% of the way there</p>
        </div>
      )}

      {/* Tier ladder */}
      {tiers.length > 1 && (
        <div className="flex gap-2 flex-wrap pt-1">
          {tiers.map((t) => (
            <span
              key={t.name}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
              style={{ backgroundColor: t.color + '20', color: t.color }}
            >
              <Award size={10} />
              {t.name}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
